import { useCallback, useMemo } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useThemeStore } from '../stores/themeStore';
import { api } from '../lib/api';
import type { LoginResponse, Role } from '@enerlyze/shared';

const ROLE_HIERARCHY: Record<Role, number> = {
  viewer: 0,
  technician: 1,
  account_manager: 2,
  master: 3,
};

export function useAuth() {
  const { user, accessToken, setAuth, setTokens, logout } = useAuthStore();

  const isAuthenticated = useMemo(() => accessToken !== null && user !== null, [accessToken, user]);

  const isMaster = useMemo(() => user?.role === 'master', [user]);
  const isAccountManager = useMemo(
    () => user?.role === 'account_manager' || user?.role === 'master',
    [user],
  );
  const isTechnician = useMemo(
    () => ROLE_HIERARCHY[user?.role ?? 'viewer'] >= ROLE_HIERARCHY.technician,
    [user],
  );

  const isAuthorized = useCallback(
    (minimumRole: Role): boolean => {
      if (!user) return false;
      return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[minimumRole];
    },
    [user],
  );

  /**
   * Stores the session only when one was issued. Otherwise the response is a
   * next step (set password, or ENER-337 two-factor), which LoginPage handles.
   */
  const login = useCallback(
    async (email: string, password: string): Promise<LoginResponse> => {
      const { data } = await api.post<LoginResponse>('/auth/login', { email, password });
      if ('accessToken' in data) setAuth(data.user, data.accessToken, data.refreshToken);
      return data;
    },
    [setAuth],
  );

  const doLogout = useCallback(async () => {
    const token = useAuthStore.getState().refreshToken;
    // Revoke refresh token server-side (best-effort, don't block logout on failure)
    if (token) {
      api.post('/auth/logout', { refreshToken: token }).catch(() => {});
    }
    logout();
    useThemeStore.getState().resetTheme();
    window.location.href = '/login';
  }, [logout]);

  const canSeeTab = useCallback(
    (tabPath: string): boolean => {
      if (!user) return false;
      if (user.role === 'master') return true;
      return user.visibleTabs?.[tabPath] !== false;
    },
    [user],
  );

  return {
    user,
    isAuthenticated,
    isMaster,
    isAccountManager,
    isTechnician,
    isAuthorized,
    canSeeTab,
    login,
    logout: doLogout,
    accessToken,
    setTokens,
  };
}
